if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.configuratorStyleContainer = document.createElement('div');
        this.configuratorStyleContainer.hidden = true;
        this.appendChild(this.configuratorStyleContainer);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.configuratorStyleContainer.innerHTML = '';
        this.modalContent.classList.remove('quick-add-configurator-content');
        this.modalContent.classList.forEach((className) => {
          if (className.startsWith('color-') || className === 'gradient') {
            this.modalContent.classList.remove(className);
          }
        });

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productInfo = responseHTML.querySelector('product-info');
            const eeConfigurator = responseHTML.querySelector('ee-configurator-product-info');
            const sectionSlice = responseHTML.querySelector('section[id^="MainProduct-"]');
            const injectionRoot = sectionSlice || eeConfigurator || productInfo;

            if (!injectionRoot) {
              super.show(opener);
              return;
            }

            const colorSource = productInfo || eeConfigurator;
            if (colorSource) {
              colorSource.classList.forEach((classApplied) => {
                if (classApplied.startsWith('color-') || classApplied === 'gradient')
                  this.modalContent.classList.add(classApplied);
              });
            }

            if (eeConfigurator) {
              return this.loadConfiguratorStyles(eeConfigurator).then(() => {
                this.preprocessHTML(injectionRoot);
                this.modalContent.classList.add('quick-add-configurator-content');
                const flashSaleHtml = this.getConfiguratorFlashSaleHTML(eeConfigurator);
                const html =
                  (sectionSlice
                    ? sectionSlice.innerHTML
                    : this.getConfiguratorQuickAddHTML(eeConfigurator)) + flashSaleHtml;
                HTMLUpdateUtility.setInnerHTML(this.modalContent, html);
                this.positionConfiguratorFlashSale();
                this.initializeConfiguratorQuickAdd();

                if (window.Shopify && Shopify.PaymentButton) {
                  Shopify.PaymentButton.init();
                }
                if (window.ProductModel) window.ProductModel.loadShopifyXR();

                super.show(opener);
              });
            }

            const originalSectionId = injectionRoot.dataset.section;
            this.preprocessHTML(injectionRoot);
            const html = sectionSlice
              ? sectionSlice.innerHTML
              : eeConfigurator
                ? this.getConfiguratorQuickAddHTML(eeConfigurator)
                : productInfo.outerHTML;

            const productInfoScriptSrc =
              productInfo?.querySelector('script[src*="product-info.js"]')?.getAttribute('src') ||
              responseHTML.querySelector('script[src*="product-info.js"]')?.getAttribute('src');

            const finalizeStandardModal = () => {
              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
              if (window.ProductModel) window.ProductModel.loadShopifyXR();
              super.show(opener);
            };

            if (sectionSlice && productInfo) {
              return this.ensureProductInfoLoaded(productInfoScriptSrc)
                .then(() => {
                  HTMLUpdateUtility.setInnerHTML(this.modalContent, html);
                  this.wrapModalContentWithProductInfo(productInfo, originalSectionId);
                  finalizeStandardModal();
                })
                .catch((error) => {
                  console.error(error);
                  HTMLUpdateUtility.setInnerHTML(this.modalContent, html);
                  finalizeStandardModal();
                });
            }

            HTMLUpdateUtility.setInnerHTML(this.modalContent, html);
            finalizeStandardModal();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        this.preventDuplicatedIDs(productElement);
        this.prepareConfiguratorQuickAddMedia(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      ensureProductInfoLoaded(scriptSrc) {
        if (customElements.get('product-info')) {
          return Promise.resolve();
        }

        if (!scriptSrc) {
          return Promise.reject(new Error('product-info.js URL not found'));
        }

        if (!this.productInfoScriptPromise) {
          this.productInfoScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = scriptSrc;
            script.defer = true;
            script.dataset.quickAddProductInfo = 'true';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load product-info.js'));
            document.head.appendChild(script);
          });
        }

        return this.productInfoScriptPromise;
      }

      wrapModalContentWithProductInfo(sourceProductInfo, originalSectionId) {
        if (!sourceProductInfo || this.modalContent.querySelector('product-info')) return;

        const wrapper = document.createElement('product-info');
        wrapper.dataset.section = `quickadd-${originalSectionId}`;
        wrapper.dataset.originalSection = originalSectionId;
        wrapper.dataset.url = sourceProductInfo.dataset.url || '';
        wrapper.dataset.updateUrl = 'false';

        if (sourceProductInfo.dataset.productId) {
          wrapper.dataset.productId = sourceProductInfo.dataset.productId;
        }

        while (this.modalContent.firstChild) {
          wrapper.appendChild(this.modalContent.firstChild);
        }

        this.modalContent.appendChild(wrapper);
      }

      prepareConfiguratorQuickAddMedia(productElement) {
        const fixedBar = productElement.querySelector('.h-20.fixed.inset-x-0.bottom-0');
        const mediaGallery = productElement.querySelector('media-gallery.product__media-gallery, .product__media-gallery');
        if (!fixedBar || !mediaGallery) return;

        const priceBlock = fixedBar.querySelector('.flex.gap-x-2.text-xl');
        if (!priceBlock || mediaGallery.querySelector('.quick-add-configurator-media-price')) return;

        const priceClone = priceBlock.cloneNode(true);
        priceClone.classList.add('quick-add-configurator-media-price');
        priceClone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

        const galleryViewer = mediaGallery.querySelector('slider-component[id^="GalleryViewer"]');
        if (galleryViewer) {
          galleryViewer.insertAdjacentElement('afterend', priceClone);
        } else {
          mediaGallery.appendChild(priceClone);
        }
      }

      initializeConfiguratorQuickAdd() {
        if (!this.modalContent.classList.contains('quick-add-configurator-content')) return;

        this.modalContent.querySelectorAll('[data-sel-open-image]').forEach((item) => {
          item.setAttribute('data-sel-open-image', 'true');
        });

        const addOnsEl = this.modalContent.querySelector('configurator-add-ons');
        if (addOnsEl?.syncAllSelectedLayers) {
          addOnsEl.syncAllSelectedLayers(true);
        }

        window.EeConfiguratorGallery?.updateImageVisibility?.(true);
        window.EeConfiguratorGallery?.requestMobileStackSync?.();

        requestAnimationFrame(() => {
          window.EeConfiguratorGallery?.updateImageVisibility?.(true);
          window.EeConfiguratorGallery?.requestMobileStackSync?.();

          const checkedInput = this.modalContent.querySelector(
            'ee-variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked'
          );
          checkedInput?.dispatchEvent(new Event('change', { bubbles: true }));

          const overlay = this.modalContent.querySelector('.variant-overlay');
          const overlayImg = this.modalContent.querySelector('.variant-overlay img');
          if (overlay && overlayImg?.getAttribute('src')) {
            overlay.classList.add('visible');
          }
        });
      }

      getConfiguratorFlashSaleHTML(configuratorElement) {
        const flashSale = configuratorElement?.querySelector('.flash-sale-message');
        if (!flashSale) return '';
        return flashSale.cloneNode(true).outerHTML;
      }

      positionConfiguratorFlashSale() {
        const flashSale = this.modalContent.querySelector('.flash-sale-message');
        if (!flashSale) return;

        const eeProduct = this.modalContent.querySelector('.ee-product');
        if (eeProduct) {
          eeProduct.insertAdjacentElement('afterend', flashSale);
        }
      }

      getConfiguratorQuickAddHTML(configuratorElement) {
        const clone = configuratorElement.cloneNode(true);
        clone.querySelector('.h-20.fixed.inset-x-0.bottom-0')?.remove();
        clone.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());
        return clone.innerHTML;
      }

      loadConfiguratorStyles(configuratorElement) {
        const stylesheetPromises = [];

        configuratorElement.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href) return;

          const alreadyLoaded = Array.from(document.querySelectorAll('link[data-quick-add-configurator]')).some(
            (loadedLink) => loadedLink.getAttribute('href') === href
          );
          if (alreadyLoaded) return;

          stylesheetPromises.push(
            new Promise((resolve) => {
              const stylesheet = document.createElement('link');
              stylesheet.rel = 'stylesheet';
              stylesheet.href = href;
              stylesheet.dataset.quickAddConfigurator = 'true';
              stylesheet.onload = () => resolve();
              stylesheet.onerror = () => resolve();
              document.head.appendChild(stylesheet);
            })
          );
        });

        this.configuratorStyleContainer.innerHTML = '';
        configuratorElement.querySelectorAll('style').forEach((style) => {
          const clonedStyle = style.cloneNode(true);
          const modalScope = 'quick-add-modal .quick-add-configurator-content';
          clonedStyle.textContent = clonedStyle.textContent
            .replace(/#EEMainProduct-[^\s{]+/g, modalScope)
            .replace(/\bsec_new_configurator\b/g, modalScope)
            .replace(/\bee-configurator-product-info\b/g, modalScope);
          this.configuratorStyleContainer.appendChild(clonedStyle);
        });

        return Promise.all(stylesheetPromises);
      }

      preventVariantURLSwitching(productElement) {
        if (productElement.tagName === 'PRODUCT-INFO' || productElement.tagName === 'EE-CONFIGURATOR-PRODUCT-INFO') {
          productElement.setAttribute('data-update-url', 'false');
          return;
        }

        productElement.querySelectorAll('variant-radios, variant-selects, ee-variant-selects').forEach((variantSwitcher) => {
          if (variantSwitcher.tagName === 'EE-VARIANT-SELECTS') {
            variantSwitcher.setAttribute('data-url', '');
          } else {
            variantSwitcher.setAttribute('data-update-url', 'false');
          }
        });
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        productElement.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());
        productElement.querySelector('.h-20.fixed.inset-x-0.bottom-0')?.remove();
        productElement.querySelector('.complementary-products')?.remove();
        if (!productElement.classList.contains('sec_new_configurator')) {
          productElement.querySelector('.product__title.desktop-hide')?.remove();
        }
        productElement.querySelectorAll('script[type="application/ld+json"]').forEach((script) => script.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        if (productElement.tagName === 'PRODUCT-INFO' || productElement.tagName === 'EE-CONFIGURATOR-PRODUCT-INFO') {
          productElement.dataset.originalSection = sectionId;
        } else {
          productElement.querySelectorAll('variant-selects, variant-radios, ee-variant-selects').forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
        }
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
